/**
 * ServiceOwnershipSection — the "Organization" card on the Service Detail
 * page: owning-team display plus claim / assign-to-team actions (issue
 * #536). Extracted from `routes/services/$serviceId.tsx` (issue #1411).
 */
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useApolloClient } from '@apollo/client/react';
import { useAllTeams } from '../../graphql/queries';
import { selectUser } from '../../store/features/auth/authSlice';
import { isAdministrator } from '../../lib/permissions';
import { sortTeamsByName } from '../../lib/usersFilter';
import { claimService, assignServiceTeam, ApiError } from '../../lib/apiClient';
import type { Service } from '../../graphql/types';

const CardElement = 'forge-card' as React.ElementType;
const DividerElement = 'forge-divider' as React.ElementType;
const ForgeButton = 'forge-button' as React.ElementType;
const BannerElement = 'forge-banner' as React.ElementType;

export interface ServiceOwnershipSectionProps {
  service: Service;
}

/** Team ownership display + claim / assign-to-team actions (issue #536). */
export function ServiceOwnershipSection({ service }: ServiceOwnershipSectionProps): JSX.Element {
  const apolloClient = useApolloClient();

  const [showOwnershipForm, setShowOwnershipForm] = useState(false);
  const [ownershipTeamId, setOwnershipTeamId] = useState('');
  const [ownershipTeamFilter, setOwnershipTeamFilter] = useState('');
  const [ownershipError, setOwnershipError] = useState<string | null>(null);
  const [isSubmittingOwnership, setIsSubmittingOwnership] = useState(false);

  // Current user (for membership-based ownership gating)
  const currentUser = useSelector(selectUser);
  const isAdmin = isAdministrator(currentUser);
  const userTeamIds = currentUser?.teams ?? [];

  // Teams for the ownership picker. Administrators may assign to any team;
  // regular members are limited to teams they belong to (issue #536).
  // `useAllTeams` auto-paginates past a single server page (issue #1354).
  const { data: allTeamsData } = useAllTeams();
  const allTeams = allTeamsData?.teams?.edges?.map((e) => e.node) ?? [];
  const ownershipTeamOptions = isAdmin
    ? allTeams
    : allTeams.filter((t) => userTeamIds.includes(t.entityId));

  // Sorted + filterable ownership team picker (issue #1271) — matches the
  // Admin Users page's AssignTeamDialog pattern (sortTeamsByName + text
  // filter) instead of a raw, unsorted <select> over connection order.
  const ownershipTeamFilterLower = ownershipTeamFilter.trim().toLowerCase();
  const filteredOwnershipTeamOptions = sortTeamsByName(
    ownershipTeamOptions.filter((t) => t.name.toLowerCase().includes(ownershipTeamFilterLower))
  );

  // Open the ownership (claim / assign-team) inline form. Preselect the only
  // eligible team when there is exactly one (issue #536).
  const handleOpenOwnershipForm = (): void => {
    setShowOwnershipForm(true);
    setOwnershipError(null);
    setOwnershipTeamFilter('');
    setOwnershipTeamId(ownershipTeamOptions.length === 1 ? ownershipTeamOptions[0].entityId : '');
  };

  const handleCancelOwnership = (): void => {
    setShowOwnershipForm(false);
    setOwnershipError(null);
    setOwnershipTeamFilter('');
  };

  // Submit claim (unowned service) or assign-team (reassign). The backend
  // enforces the real authorization; this only drives the UX.
  const handleSubmitOwnership = (): void => {
    if (!ownershipTeamId) return;
    setIsSubmittingOwnership(true);
    setOwnershipError(null);
    const isUnowned = !service.teamId;
    const request = isUnowned
      ? claimService(service.entityId, ownershipTeamId)
      : assignServiceTeam(service.entityId, ownershipTeamId);
    request
      .then(() => {
        setShowOwnershipForm(false);
        setOwnershipTeamId('');
        setOwnershipTeamFilter('');
        void apolloClient.refetchQueries({ include: ['GetService'] });
      })
      .catch((err) => {
        setOwnershipError(
          err instanceof ApiError ? err.message : 'Failed to update service ownership'
        );
      })
      .finally(() => setIsSubmittingOwnership(false));
  };

  return (
    <CardElement className="service-card">
      <div className="card-header">
        <h2 className="forge-typography--headline6">Organization</h2>
      </div>
      <DividerElement />
      <div className="card-content">
        <div className="detail-row">
          <span className="detail-label">Team</span>
          <span className="detail-value" data-testid="service-team-value">
            {service.teamId ? service.teamId : <span className="detail-secondary">Unassigned</span>}
          </span>
        </div>

        {/* Ownership actions — claim / assign to team (issue #536).
            Backend enforces the real authorization; this is a UX gate. */}
        {(isAdmin || userTeamIds.length > 0) && (
          <>
            {ownershipError && (
              <BannerElement
                theme="error"
                dismissed={false}
                role="alert"
                className="ownership-banner"
                data-testid="ownership-error"
              >
                <span>{ownershipError}</span>
              </BannerElement>
            )}

            {!showOwnershipForm && (
              <div className="ownership-actions">
                <ForgeButton
                  type="button"
                  variant="outlined"
                  onClick={handleOpenOwnershipForm}
                  data-testid={service.teamId ? 'assign-team-btn' : 'claim-service-btn'}
                >
                  <forge-icon
                    name={service.teamId ? 'swap_horiz' : 'flag'}
                    slot="start"
                    aria-hidden="true"
                  ></forge-icon>
                  {service.teamId ? 'Assign to Team' : 'Claim Service'}
                </ForgeButton>
              </div>
            )}

            {showOwnershipForm && (
              <div className="ownership-form" data-testid="ownership-form">
                <div className="ownership-form-field">
                  <label htmlFor="ownership-team-filter" className="forge-typography--caption">
                    Filter teams
                  </label>
                  <input
                    id="ownership-team-filter"
                    type="text"
                    className="filter-select"
                    placeholder="Filter by team name…"
                    value={ownershipTeamFilter}
                    onChange={(e) => {
                      setOwnershipTeamFilter(e.target.value);
                      setOwnershipTeamId('');
                    }}
                    aria-label="Filter teams by name"
                    data-testid="ownership-team-filter-input"
                  />
                </div>

                <div className="ownership-form-field">
                  <label htmlFor="ownership-team" className="forge-typography--caption">
                    {service.teamId ? 'Assign to team' : 'Claim for team'}
                  </label>
                  <select
                    id="ownership-team"
                    className="filter-select"
                    value={ownershipTeamId}
                    onChange={(e) => setOwnershipTeamId(e.target.value)}
                    aria-label="Select team"
                    data-testid="ownership-team-select"
                  >
                    <option value="">— Select a team —</option>
                    {filteredOwnershipTeamOptions.map((t) => (
                      <option key={t.entityId} value={t.entityId}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  {filteredOwnershipTeamOptions.length === 0 && ownershipTeamFilter.trim() && (
                    <p className="forge-typography--caption page-header-subtitle">
                      No teams match your search.
                    </p>
                  )}
                </div>
                <div className="ownership-form-actions">
                  <ForgeButton
                    type="button"
                    variant="raised"
                    disabled={isSubmittingOwnership || !ownershipTeamId}
                    onClick={handleSubmitOwnership}
                    data-testid="ownership-submit"
                  >
                    {service.teamId ? 'Assign' : 'Claim'}
                  </ForgeButton>
                  <ForgeButton
                    type="button"
                    variant="outlined"
                    onClick={handleCancelOwnership}
                    data-testid="ownership-cancel"
                  >
                    Cancel
                  </ForgeButton>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </CardElement>
  );
}
